import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SEO = ({ 
  title,
  description,
  image,
  article = false,
  noindex = false
}) => {
  const location = useLocation();
  const siteUrl = typeof window !== 'undefined' ? window.location.origin : 'https://jenniferbrantleylaw.com';
  const currentUrl = `${siteUrl}${location.pathname}`;
  
  // Default values
  const siteName = "J. Brantley Law, PLLC";
  const defaultTitle = "J. Brantley Law | The Fine Print Lawyer";
  const defaultDescription = "Strategic legal counsel for builders, brands, and businesses. Licensed in Georgia and Texas. Trademark registration, business formation, contracts, and more.";
  const defaultImage = `${siteUrl}/og-image.jpg`;
  
  const seoTitle = title ? `${title} | ${siteName}` : defaultTitle;
  const seoDescription = description || defaultDescription;
  const seoImage = image || defaultImage;
  const seoUrl = currentUrl;

  useEffect(() => {
    // Update document title
    document.title = seoTitle;

    // Helper to update or create meta tag
    const updateMeta = (selector, attribute, content) => {
      let element = document.querySelector(selector);
      if (!element) {
        element = document.createElement('meta');
        if (selector.includes('property=')) {
          element.setAttribute('property', selector.match(/property="([^"]+)"/)[1]);
        } else if (selector.includes('name=')) {
          element.setAttribute('name', selector.match(/name="([^"]+)"/)[1]);
        }
        document.head.appendChild(element);
      }
      element.setAttribute(attribute, content);
    };

    // Update meta tags
    updateMeta('meta[name="description"]', 'content', seoDescription);
    updateMeta('meta[name="robots"]', 'content', noindex ? 'noindex, nofollow' : 'index, follow');
    updateMeta('meta[name="author"]', 'content', 'Jennifer N. Brantley');
    
    // Open Graph
    updateMeta('meta[property="og:title"]', 'content', seoTitle);
    updateMeta('meta[property="og:description"]', 'content', seoDescription);
    updateMeta('meta[property="og:url"]', 'content', seoUrl);
    updateMeta('meta[property="og:type"]', 'content', article ? 'article' : 'website');
    updateMeta('meta[property="og:image"]', 'content', seoImage);
    updateMeta('meta[property="og:site_name"]', 'content', siteName);
    
    // Twitter
    updateMeta('meta[name="twitter:card"]', 'content', 'summary_large_image');
    updateMeta('meta[name="twitter:title"]', 'content', seoTitle);
    updateMeta('meta[name="twitter:description"]', 'content', seoDescription);
    updateMeta('meta[name="twitter:image"]', 'content', seoImage);

    // Update canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', seoUrl);

    // Add Schema.org JSON-LD
    const schemaId = 'seo-schema-jsonld';
    let schemaScript = document.getElementById(schemaId);
    if (!schemaScript) {
      schemaScript = document.createElement('script');
      schemaScript.id = schemaId;
      schemaScript.type = 'application/ld+json';
      document.head.appendChild(schemaScript);
    }

    const schemaOrgJSONLD = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "LegalService",
          "@id": `${siteUrl}/#organization`,
          "name": "J. Brantley Law, PLLC",
          "alternateName": "The Fine Print Lawyer",
          "url": siteUrl,
          "logo": `${siteUrl}/logo.png`,
          "image": seoImage,
          "description": seoDescription,
          "telephone": "+1-404-555-0123",
          "email": "jbrantley@jenniferbrantleylaw.com",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Atlanta",
            "addressRegion": "GA",
            "addressCountry": "US"
          },
          "areaServed": [
            { "@type": "State", "name": "Georgia" },
            { "@type": "State", "name": "Texas" },
            { "@type": "Country", "name": "United States", "description": "Trademark services in all 50 states" }
          ],
          "priceRange": "$$",
          "openingHoursSpecification": {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            "opens": "09:00",
            "closes": "17:00"
          },
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Legal Services",
            "itemListElement": [
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Trademark Search & Strategy" } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Trademark Filing" } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Business Formation" } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Contract Drafting & Review" } }
            ]
          }
        },
        {
          "@type": "Attorney",
          "@id": `${siteUrl}/#attorney`,
          "name": "Jennifer N. Brantley",
          "jobTitle": "Attorney at Law",
          "worksFor": { "@id": `${siteUrl}/#organization` },
          "knowsAbout": ["Trademark Law", "Business Formation", "Contract Law", "Intellectual Property"]
        },
        {
          "@type": "WebSite",
          "@id": `${siteUrl}/#website`,
          "url": siteUrl,
          "name": siteName,
          "description": seoDescription,
          "publisher": { "@id": `${siteUrl}/#organization` }
        },
        {
          "@type": "WebPage",
          "@id": seoUrl,
          "url": seoUrl,
          "name": seoTitle,
          "description": seoDescription,
          "isPartOf": { "@id": `${siteUrl}/#website` }
        }
      ]
    };

    schemaScript.textContent = JSON.stringify(schemaOrgJSONLD);

  }, [seoTitle, seoDescription, seoImage, seoUrl, article, noindex, siteUrl, siteName]);

  // This component doesn't render anything visible
  return null;
};

export default SEO;
